import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "../components/Seo";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

const LINK = /\[([^\]]+)\]\(([^)]+)\)/g;

const inline = (text) => {
  const out = [];
  let last = 0;
  let m;
  while ((m = LINK.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <a key={m.index} href={m[2]} className="text-[#ffc76b] underline-offset-4 hover:underline">
        {m[1]}
      </a>
    );
    last = m.index + m[0].length;
  }
  LINK.lastIndex = 0;
  if (last < text.length) out.push(text.slice(last));
  return out.map((part, j) =>
    typeof part === "string"
      ? part.split("**").map((s, k) => (k % 2 ? <strong key={`${j}-${k}`} className="text-white">{s}</strong> : s))
      : part
  );
};

const render = (body) =>
  body.split("\n\n").map((block, i) => {
    if (block.startsWith("### "))
      return (
        <h3 key={i} className="mt-9 font-display text-lg font-semibold sm:text-xl">
          {block.slice(4)}
        </h3>
      );
    if (block.startsWith("## "))
      return (
        <h2 key={i} className="mt-12 font-display text-2xl font-semibold sm:text-3xl">
          {block.slice(3)}
        </h2>
      );
    if (block.trim().startsWith("- "))
      return (
        <ul key={i} className="mt-6 space-y-3">
          {block.split("\n").map((li, j) => (
            <li key={j} className="flex gap-3 text-[15px] leading-relaxed text-[#c0c3d0]">
              <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[#ff4b2b]" />
              <span>{inline(li.replace(/^-\s*/, ""))}</span>
            </li>
          ))}
        </ul>
      );
    return (
      <p key={i} className="mt-6 text-[15.5px] leading-[1.85] text-[#c0c3d0]">
        {inline(block)}
      </p>
    );
  });

export function Blog() {
  const { locale } = useI18n();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get(`/blog?locale=${locale}`).then(({ data }) => setPosts(data.posts)).catch(() => {});
  }, [locale]);

  return (
    <div className="pt-16" data-testid="blog-page">
      <Seo
        title={locale === "fr" ? "Blog — Studio Movies IA" : "Blog — Studio Movies IA"}
        description={locale === "fr"
          ? "Guides, prompts cinématographiques, workflows de production IA et comparaisons de modèles."
          : "Guides, cinematic prompts, AI production workflows and model comparisons."}
        locale={locale}
        path={`/${locale}/blog`}
        altPath={`/${locale === "en" ? "fr" : "en"}/blog`}
      />
      <section className="px-6 pb-24 pt-20 lg:px-10 lg:pt-28">
        <div className="mx-auto max-w-[1400px]">
          <p className="overline">Editorial</p>
          <h1 className="mt-4 font-display text-4xl font-semibold sm:text-5xl lg:text-6xl">
            {locale === "fr" ? "Le journal du studio" : "The studio journal"}
          </h1>
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/${locale}/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="hot-border group overflow-hidden rounded-xl border border-[#212230] bg-[#0b0b11]"
              >
                <img
                  src={p.cover}
                  alt={p.title}
                  loading="lazy"
                  className="h-[180px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="p-6">
                  <p className="mono text-[10px] uppercase tracking-[0.16em] text-[#ff4b2b]">
                    {p.tags?.join(" · ")}
                  </p>
                  <h2 className="mt-3 font-display text-lg font-medium leading-snug">{p.title}</h2>
                  <p className="mt-3 line-clamp-3 text-[13px] leading-relaxed text-[#9194a6]">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { locale } = useI18n();
  const [p, setP] = useState(null);

  useEffect(() => {
    api.get(`/blog/${locale}/${slug}`).then(({ data }) => setP(data)).catch(() => setP(false));
  }, [locale, slug]);

  if (!p) return <div className="px-6 pt-48 text-center text-[#9194a6]">…</div>;

  return (
    <div className="pt-16" data-testid="blog-post">
      <Seo
        title={p.seo_title}
        description={p.seo_description}
        locale={locale}
        path={`/${locale}/blog/${p.slug}`}
        image={p.cover}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: p.title,
          image: p.cover,
          author: { "@type": "Organization", name: p.author },
          datePublished: p.published_at,
        }}
      />
      <article className="px-6 pb-28 pt-16 lg:px-10">
        <div className="mx-auto max-w-[820px]">
          <Link to={`/${locale}/blog`} className="mono text-[11px] uppercase tracking-[0.16em] text-[#ff4b2b]">
            ← Blog
          </Link>
          <h1 className="mt-7 font-display text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
            {p.title}
          </h1>
          <p className="mono mt-5 text-[11px] uppercase tracking-[0.16em] text-[#8288a0]">
            {p.author} · {p.tags?.join(" · ")}
          </p>
          <img src={p.cover} alt={p.title} className="mt-10 h-[320px] w-full rounded-2xl object-cover" />
          <div className="mt-4">{render(p.body)}</div>

          <div className="mt-16 rounded-2xl border border-[#212230] bg-[#0b0b11] p-8">
            <p className="font-display text-xl font-semibold">
              {locale === "fr" ? "Passez de la théorie au plateau." : "Move from theory to the set."}
            </p>
            <Link
              to={`/${locale}/register`}
              className="btn-amber mt-6 inline-block rounded-full px-6 py-3 text-sm font-semibold"
            >
              {locale === "fr" ? "Créer votre première scène" : "Create your first scene"}
            </Link>
          </div>

          {p.related?.length > 0 && (
            <div className="mt-16">
              <p className="overline">{locale === "fr" ? "À lire ensuite" : "Read next"}</p>
              <div className="mt-6 space-y-3">
                {p.related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/${locale}/blog/${r.slug}`}
                    className="block rounded-xl border border-[#212230] bg-[#0b0b11] p-5 text-[14px] transition-colors duration-200 hover:border-[#383b4d]"
                  >
                    {r.title}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
}

export function LegalPage() {
  const { slug } = useParams();
  const { locale } = useI18n();
  const [p, setP] = useState(null);

  useEffect(() => {
    api.get(`/legal/${locale}/${slug}`).then(({ data }) => setP(data)).catch(() => setP(false));
  }, [locale, slug]);

  if (p === false) {
    return (
      <div className="pt-16" data-testid="legal-not-found">
        <div className="mx-auto max-w-[720px] px-6 pb-28 pt-24 text-center lg:px-10">
          <p className="overline">404</p>
          <h1 className="mt-4 font-display text-3xl font-semibold sm:text-4xl">
            {locale === "fr" ? "Document introuvable" : "Document not found"}
          </h1>
          <p className="mt-5 text-sm leading-relaxed text-[#9194a6]">
            {locale === "fr"
              ? "Cette page légale n'existe pas ou a été renommée."
              : "This legal page does not exist or has been renamed."}
          </p>
          <Link to={`/${locale}`} className="btn-amber mt-9 inline-flex rounded-full px-6 py-3 text-[13px] font-semibold">
            {locale === "fr" ? "Retour à l'accueil" : "Back to home"}
          </Link>
        </div>
      </div>
    );
  }

  if (!p) return <div className="px-6 pt-48 text-center text-[#9194a6]">…</div>;

  return (
    <div className="pt-16" data-testid="legal-page">
      <Seo title={`${p.title} — Studio Movies IA`} description={p.title} locale={locale}
           path={`/${locale}/legal/${p.slug}`} />
      <div className="mx-auto max-w-[820px] px-6 pb-28 pt-16 lg:px-10">
        <p className="overline">Legal</p>
        <h1 className="mt-4 font-display text-4xl font-semibold">{p.title}</h1>
        <div className="mt-8">{render(p.body)}</div>
      </div>
    </div>
  );
}
